import { useRef } from "react";
import { Download, FileText, LoaderCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { PROFILE_FORM_STYLES } from "@/components/Forms/profile-form-styles";
import { cn } from "@/lib/utils";
import {
  credentialDocumentFormat,
  isCredentialDocumentImage,
} from "../../lib/credential-document-format";
import Loading from "@/components/Loading";
import PDFViewer from "@/components/PDFViewer/PDFViewer";
import ImageViewer from "@/components/MediaViewer/ImageViewer";
import type { PreviewDocument } from "../../hooks/use-credential-document-url";

/**
 * Preview a credential document, with download in the header.
 *
 * Uses the same `PDFViewer` / `ImageViewer` the legacy file modal uses, so a
 * document looks the same wherever it is opened. It does NOT reuse
 * `FilePreviewModal` itself: that renders `FileVersionPreview`, which is built
 * on the legacy model — it queries `credentialFiles.getFilesCheckPermission`,
 * offers delete and snooze against legacy rows, and takes a legacy `FileType`
 * keyed by `user_credentials_file_id`. Wiring the new page to it would
 * re-couple this surface to the tables O5 retires, and it would not work at all
 * for a document an organization uploads directly, which has no legacy row.
 */
export function CredentialDocumentPreviewModal({
  document,
  description,
  url,
  isLoading,
  isDownloading = false,
  error,
  onDownload,
  onReview,
  onClose,
}: {
  document: PreviewDocument;
  description?: string;
  /** Signed, short-lived. Absent while it is being minted. */
  url: string | null;
  isLoading: boolean;
  isDownloading?: boolean;
  error?: string | null;
  onDownload: () => void;
  onReview?: () => void;
  onClose: () => void;
}) {
  const openerRef = useRef<HTMLElement | null>(null);
  const name = document.fileName || document.title || "Document";
  const format = credentialDocumentFormat(name);
  const viewerHeight = "h-full min-h-0 rounded-none";
  let content;
  if (error) {
    content = (
      <div
        role="alert"
        className="flex h-full items-center justify-center p-8 text-center text-sm text-red-700"
      >
        {error}
      </div>
    );
  } else if (isLoading || !url) {
    content = (
      <div
        role="status"
        className="flex h-full flex-col items-center justify-center gap-3 text-sm text-slate-500"
      >
        <Loading className="size-8" />
        Loading document…
      </div>
    );
  } else if (format === "pdf") {
    content = (
      <PDFViewer
        key={url}
        url={url}
        fitPage
        containerClassName={viewerHeight}
      />
    );
  } else if (isCredentialDocumentImage(name)) {
    content = (
      <ImageViewer
        key={url}
        url={url}
        alt={document.title || name}
        containerClassName={viewerHeight}
      />
    );
  } else {
    content = (
      <div className="flex h-full flex-col items-center justify-center gap-4 p-8 text-center text-sm text-slate-500">
        <FileText className="size-12 text-slate-400" aria-hidden="true" />
        <p>
          This file type can’t be displayed here.
          <br />
          Download the original to open it.
        </p>
        <Button
          type="button"
          variant="outline"
          className="gap-2 rounded-xl"
          disabled={isDownloading}
          onClick={onDownload}
        >
          <Download className="size-4" aria-hidden="true" />
          Download original
        </Button>
      </div>
    );
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        // Keep credential evidence out of Sentry session replays.
        data-sentry-block
        onOpenAutoFocus={() => {
          if (window.document.activeElement instanceof HTMLElement) {
            openerRef.current = window.document.activeElement;
          }
        }}
        onCloseAutoFocus={(event) => {
          // This dialog is opened programmatically, without a Radix trigger.
          // Restore within the current parent, including a newly opened review
          // editor, instead of allowing focus to fall behind the vault.
          const parentDialog = Array.from(
            window.document.querySelectorAll<HTMLElement>(
              '[role="dialog"][data-state="open"]',
            ),
          ).at(-1);
          event.preventDefault();
          if (
            openerRef.current?.isConnected &&
            (!parentDialog || parentDialog.contains(openerRef.current))
          ) {
            openerRef.current.focus();
          } else {
            parentDialog?.focus();
          }
        }}
        className={cn(
          PROFILE_FORM_STYLES.dialog,
          "[&.dialog-content]:h-[90dvh] [&.dialog-content]:max-h-[90dvh] sm:[&.dialog-content]:max-w-6xl",
        )}
      >
        <DialogHeader
          className={cn(
            PROFILE_FORM_STYLES.header,
            "px-5 py-5 sm:px-6 sm:pr-16",
          )}
        >
          <div className="flex flex-wrap items-start gap-3 sm:gap-4">
            <div className={cn(PROFILE_FORM_STYLES.icon, "hidden sm:flex")}>
              <FileText className="size-5" aria-hidden="true" />
            </div>
            <div className="min-w-0 flex-1 space-y-1.5 pr-10 sm:pr-0">
              <p className={PROFILE_FORM_STYLES.eyebrow}>
                Document preview{format && ` · ${format.toUpperCase()}`}
              </p>
              <DialogTitle className="break-words text-left text-lg font-bold leading-snug tracking-tight text-slate-900 sm:text-xl">
                {document.title || name}
              </DialogTitle>
              <DialogDescription className="break-all text-left text-xs text-slate-500">
                {description ?? name}
              </DialogDescription>
            </div>
            <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
              {onReview && (
                <Button
                  type="button"
                  size="sm"
                  className="h-9 shrink-0 rounded-xl"
                  disabled={isLoading || isDownloading}
                  onClick={onReview}
                >
                  Review upload
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-9 shrink-0 gap-2 rounded-xl text-primary"
                disabled={isLoading || !url || isDownloading}
                onClick={onDownload}
              >
                {isDownloading ? (
                  <LoaderCircle
                    className="size-4 animate-spin"
                    aria-hidden="true"
                  />
                ) : (
                  <Download className="size-4" aria-hidden="true" />
                )}
                <span>{isDownloading ? "Downloading…" : "Download"}</span>
              </Button>
            </div>
          </div>
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-hidden bg-slate-100">
          {content}
        </div>
      </DialogContent>
    </Dialog>
  );
}
